import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { registry } from '../../core/Registry.js';

const Wrapper = styled.div`
  width: ${props => props.width || '100%'};
  display: flex;
  align-items: stretch;
  background: var(--glass-1);
  border: 1px solid var(--glass-border);
  border-radius: 16px;
  overflow: hidden;
  backdrop-filter: blur(10px);
  
  @media(max-width: 768px) {
    flex-direction: column;
  }
`;

const ImgBox = styled.div`
  flex: 1;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  position: relative;

  img {
    max-width: 150px;
    filter: drop-shadow(0 20px 30px rgba(0,0,0,0.5));
    transition: transform 0.3s;
    &:hover { transform: scale(1.05); }
  }

  .sim-icon {
    position: absolute;
    bottom: 30px;
    right: 30px;
    width: 60px;
    height: 80px;
    background: linear-gradient(135deg, var(--accent), var(--accent-neon));
    border-radius: 8px 30px 8px 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-weight: bold;
    box-shadow: 0 10px 20px var(--accent-glow);
    font-size: 24px;
    border: 2px solid rgba(255,255,255,0.2);
  }
`;

const ContentBox = styled.div`
  flex: 1;
  padding: 40px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Badge = styled.div`
  display: inline-block;
  background: var(--accent);
  color: #fff;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: bold;
  text-transform: uppercase;
  margin-bottom: 15px;
  align-self: flex-start;
`;

const Title = styled.h2`
  margin: 0 0 10px 0;
  color: var(--text-primary);
  font-size: 28px;
`;

const Desc = styled.p`
  margin: 0 0 30px 0;
  color: var(--text-secondary);
  line-height: 1.5;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 20px;
`;

const Price = styled.div`
  font-size: 42px;
  font-weight: 900;
  color: var(--text-primary);
  
  span { font-size: 16px; font-weight: normal; color: var(--text-muted); }
`;

const Features = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 30px 0;
  li {
    position: relative;
    padding-left: 25px;
    margin-bottom: 10px;
    color: var(--text-secondary);
    font-size: 14px;
    &::before {
      content: '✓';
      position: absolute;
      left: 0;
      color: var(--accent);
      font-weight: bold;
    }
  }
`;

const Button = styled.button`
  background: linear-gradient(135deg, var(--accent), var(--accent-neon));
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 16px 30px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  box-shadow: 0 4px 20px var(--accent-glow);
`;

const HardwareBundleWidget = ({ node }) => {
  const [device, setDevice] = useState(null);
  const [tariff, setTariff] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch('/api/smartphones').then(r => r.json()).catch(() => []),
      fetch('/api/tariffs').then(r => r.json()).catch(() => [])
    ])
    .then(([d, t]) => {
      const devs = Array.isArray(d) ? d : (d.smartphones || []);
      const tars = Array.isArray(t) ? t : (t.tariffs || []);
      
      setDevice(devs.find(x => x.brand === 'Apple') || devs[0] || null);
      setTariff(tars.find(x => x.status === 'active') || tars[0] || null);
      setLoading(false);
    });
  }, []);

  const title = node.content?.title || (device && tariff ? `${device.name} + ${tariff.title || tariff.name}` : 'Loading Bundle...');
  const desc = node.content?.desc || (device ? `Sichere dir das neue ${device.name} im besten O2 Netz.` : 'Das absolute Power-Bundle für alle.');
  const imgUrl = node.content?.img || (device?.image || 'https://via.placeholder.com/300x600/1e293b/00A9E0?text=Smart+Phone');
  
  // Berechne Dummy-Bundle Preis
  let monthlyPrice = 49.99;
  if (device && tariff) {
    const devPrice = Object.values(device.prices || {})[0] || 0;
    const tPrice = tariff.promoPrice || tariff.price || 0;
    // Angenommener Bundle-Rabatt
    monthlyPrice = Math.max(9.99, (devPrice / 24) + tPrice - 10).toFixed(2);
  } else if (!loading) {
    monthlyPrice = '49,99';
  } else {
    monthlyPrice = '...';
  }

  return (
    <Wrapper width={node.style?.desktop?.width}>
      <ImgBox>
        <img src={imgUrl} alt="Hardware" style={{ objectFit: 'contain', maxHeight: 250 }} />
        <div className="sim-icon">O₂</div>
      </ImgBox>
      <ContentBox>
        <Badge>Tipp der Woche</Badge>
        <Title>{title}</Title>
        <Desc>{desc}</Desc>
        
        <Features>
          {device?.storage && <li>Verschiedene Speichervarianten verfügbar</li>}
          <li>{tariff ? `${tariff.dataLabel} GB Highspeed-Datenvolumen` : 'Highspeed-Datenvolumen'}</li>
          <li>{tariff ? `${tariff.speed} Mbit/s 5G fähig` : 'Mobiles Internet'}</li>
        </Features>
        
        <PriceRow>
          <Price>{monthlyPrice} € <span>mtl.</span></Price>
        </PriceRow>
        
        <Button className="tef-bubbles">Jetzt Bundle sichern</Button>
      </ContentBox>
    </Wrapper>
  );
};

registry.register('hardware_bundle', {
  label: 'Hardware Bundle',
  icon: '🎁',
  category: 'tef_specific',
  defaultContent: {},
  defaultStyle: { desktop: { width: '100%' } },
  render: HardwareBundleWidget,
  inspectorControls: [
    { tab: 'content', groups: [{ title: 'Inhalte', fields: [{name:'content.title', label:'Titel', type:'text'}, {name:'content.desc', label:'Beschreibung', type:'textarea'}, {name:'content.img', label:'Bild URL', type:'text'}] }] },
    { tab: 'style', groups: [{ title: 'Layout', fields: [{name:'style.desktop.width', label:'Max Breite', type:'text'}] }] }
  ]
});
